import { Link } from 'react-router';
import { BlogPostLayout } from '@/components/blog/blog-post-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function PotholeClaimEvidenceBlogPost() {
  return (
    <BlogPostLayout
      slug="what-evidence-do-you-need-for-pothole-damage-claim"
      title="What Evidence Do You Need for a Successful Pothole Damage Claim?"
      description="A complete UK pothole damage claim evidence checklist covering pothole photos, vehicle damage, garage invoices, prior reports, and council records."
      category="Evidence and Documentation"
      readTime="7 min"
      datePublished="2026-02-07"
      dateModified="2026-02-07"
      image={{
        src: '/blog/pothole-claim-evidence-1200.webp',
        srcSet:
          '/blog/pothole-claim-evidence-800.webp 800w, /blog/pothole-claim-evidence-1200.webp 1200w',
        sizes: '(min-width: 1024px) 896px, calc(100vw - 32px)',
        alt: 'Driver photographing pothole damage beside a car wheel, showing evidence collection for a UK pothole damage claim.',
        width: 1200,
        height: 630,
        caption:
          'Photographs of the pothole, vehicle damage and location are often the backbone of a successful pothole damage claim.',
      }}
      keywords={[
        'pothole claim evidence',
        'pothole damage photos',
        'repair invoice',
        'council inspection records',
        'Section 58 defence',
      ]}
      geoFocus={['England', 'Wales', 'UK local councils']}
      atAGlance={[
        { label: 'Core proof', value: 'Show the pothole existed, caused the damage, and what the repair cost.' },
        { label: 'Most important evidence', value: 'Clear pothole photos with scale, location context, and timestamped damage photos.' },
        { label: 'Strongest extra evidence', value: 'Prior reports or council records showing the defect was known before your incident.' },
        { label: 'Biggest mistake', value: 'Repairing the vehicle before photographing the pothole and damage.' },
      ]}
      sources={[
        { name: 'Highways Act 1980, Section 41', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/41' },
        { name: 'Highways Act 1980, Section 58', url: 'https://www.legislation.gov.uk/ukpga/1980/66/section/58' },
        { name: 'Freedom of Information Act 2000', url: 'https://www.legislation.gov.uk/ukpga/2000/36/contents' },
        { name: 'FixMyStreet public street reports', url: 'https://www.fixmystreet.com/' },
      ]}
      faqs={[
        {
          question: 'What evidence do I need for a pothole damage claim?',
          answer:
            'You need photos of the pothole, photos of the location, photos of the vehicle damage, a written garage quote or invoice, records of related costs, a clear incident note, and any evidence that the pothole was previously reported.',
        },
        {
          question: 'Is a repair invoice enough for a pothole claim?',
          answer:
            'Usually not on its own. A repair invoice proves cost, but you also need to show what caused the damage. Pothole photos, location evidence, and a mechanic note linking the damage to impact all strengthen the claim.',
        },
        {
          question: 'Can prior pothole reports help my claim?',
          answer:
            'Yes. If the pothole was reported before your incident and left unrepaired, that evidence can weaken a council argument that it had a reasonable inspection and repair system in place.',
        },
      ]}
      relatedPosts={[
        {
          slug: 'new-year-same-potholes',
          title: 'New Year, Same Potholes',
          description: 'What to do in the first minutes after January pothole vehicle damage.',
          readTime: '6 min',
        },
      ]}
    >
      <p>
        Pothole claims are not won on how angry you are. They are won on evidence. The council does
        not know you, does not see your damaged wheel, and starts from the position of paying
        nothing. Your evidence is the only thing that moves them. Get it right and a claim becomes
        hard to refuse. Get it wrong, or leave gaps, and even a genuine claim gets rejected on a
        technicality.
      </p>

      <p>
        This is the complete checklist of what you need, why each piece matters, and the mistakes
        that sink otherwise valid claims.
      </p>

      <h2>Why Evidence Is Everything</h2>

      <p>
        When you claim for pothole damage, the burden is on you to show three things: that the
        pothole existed, that it caused your damage, and what putting that damage right has cost. The
        council, for its part, will look for any reason to say no. The most common defence is that
        they had a reasonable system of inspection and repair in place, which can absolve them even
        when a pothole slipped through.
      </p>

      <p>
        That defence is beatable, but only with evidence. Every item below exists to close off a
        route the council might use to reject you. Fixtyer's guide to the{' '}
        <Link to="/guides/section-58-defence" className={inlineLink}>
          Section 58 defence
        </Link>{' '}
        explains why inspection and repair records matter so much.
      </p>

      <h2>1. Photographs of the Pothole</h2>

      <p>
        This is the single most important piece, and the one most often missing, because by the time
        people think to claim, the hole has been filled.
      </p>

      <p>Your photos should show:</p>

      <ul>
        <li>The pothole itself, clearly and in focus.</li>
        <li>
          <strong>Scale.</strong> Place something next to it for size: a shoe, a drink can, or a
          tape measure if you have one. Depth is critical, because councils frequently argue a
          pothole was too shallow to be actionable. Many authorities use an informal threshold of
          around 40mm depth before they treat a defect as a priority, so showing genuine depth
          matters.
        </li>
        <li>
          <strong>Location context.</strong> Take a wider shot capturing the road, nearby signs,
          kerbs or landmarks, so the exact spot can be pinpointed later.
        </li>
      </ul>

      <p>
        Take these at the scene if it is safe. If you could not stop safely, return in daylight and
        photograph the pothole before it is repaired. Your phone timestamps and geotags images
        automatically, which quietly corroborates when and where they were taken. For a deeper
        walkthrough, use the guide to{' '}
        <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
          photographing pothole damage
        </Link>
        .
      </p>

      <h2>2. Photographs of the Damage to Your Vehicle</h2>

      <p>
        Photograph the damage clearly and from more than one angle: the buckled alloy, the split or
        bulging tyre, the scrape along the underside, whatever you are claiming for. Where you can,
        capture the damaged part and the pothole in a way that visibly connects the two. The link
        between cause and damage is exactly what the council will try to break, so anything that ties
        them together is valuable.
      </p>

      <p>
        Fixtyer's guide to{' '}
        <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
          documenting vehicle damage
        </Link>{' '}
        covers the kinds of wheel, tyre, suspension, and alignment evidence worth keeping.
      </p>

      <h2>3. A Repair Quote or Invoice</h2>

      <p>You need to show what the damage costs to fix. A written quote or invoice from a garage should:</p>

      <ul>
        <li>Describe the specific damage and the work required.</li>
        <li>
          Ideally note that the damage is consistent with impact, rather than general wear and tear.
        </li>
      </ul>

      <p>
        That second point matters more than it looks. The council's favourite argument is that your
        damage was pre-existing or down to normal wear. A line from a mechanic stating the damage is
        consistent with a sudden impact directly counters that. Keep every related receipt too:
        replacement tyres, recovery costs if the car was undriveable, and a hire car if you
        genuinely needed one.
      </p>

      <p>
        Before you submit, compare your garage paperwork against the guide to{' '}
        <Link to="/guides/getting-repair-quotes" className={inlineLink}>
          getting the right repair quote
        </Link>
        .
      </p>

      <h2>4. Evidence the Council Knew, or Should Have Known</h2>

      <p>
        This is the piece that turns a decent claim into a strong one, and most drivers never gather
        it.
      </p>

      <p>
        The council's main defence rests on having a reasonable inspection regime. You undercut that
        defence by showing the pothole had already been reported and left unrepaired. If a dangerous
        defect was flagged to the authority weeks or months before you hit it and nothing was done,
        their "reasonable system" argument weakens considerably.
      </p>

      <p>
        So it is worth checking whether the pothole was reported before your incident. Public
        street-reporting tools and the council's own records can reveal a defect's history. You can
        also submit a Freedom of Information request to the council asking when the pothole was first
        reported and what inspection and repair history exists for that stretch of road. A documented
        history of inaction is powerful evidence.
      </p>

      <p>
        Start with Fixtyer's guide to{' '}
        <Link to="/guides/prior-reports-explained" className={inlineLink}>
          prior reports
        </Link>{' '}
        if you want to understand how this evidence affects a council's response.
      </p>

      <h2>5. A Clear Record of the Incident</h2>

      <p>
        Write down, while it is fresh, the facts of what happened: the exact location, the date and
        time, the direction you were travelling, the weather and road conditions, and how the impact
        felt and sounded. This is not legal proof on its own, but a consistent, detailed account
        given early is more credible than a vague recollection assembled months later. If there were
        any witnesses or passengers, note them too.
      </p>

      <h2>6. Your Report to the Council</h2>

      <p>
        Report the pothole to the responsible authority, and keep a copy of that report and any
        reference number. Reporting does two jobs: it creates a dated record, and it strengthens the
        duty on the authority going forward. It also signals you are dealing with this properly
        rather than chancing it.
      </p>

      <p>
        If you are not sure which authority is responsible, start with the guide to{' '}
        <Link to="/guides/who-is-responsible" className={inlineLink}>
          who is responsible for the road
        </Link>
        .
      </p>

      <h2>The Mistakes That Sink Valid Claims</h2>

      <p>Even strong claims fail on avoidable errors. The big ones:</p>

      <ul>
        <li>
          <strong>Fixing the car before photographing the damage.</strong> Once it is repaired, your
          proof is gone. Photograph first, fix second.
        </li>
        <li>
          <strong>No photo of the pothole.</strong> A receipt and your word is weak. The pothole
          image, with scale, is the backbone of the claim.
        </li>
        <li>
          <strong>Waiting too long.</strong> Evidence decays. Potholes get filled, memories fade,
          and a fresh claim always beats a stale one. While the formal limitation period for this
          kind of claim can extend to six years, treating it as a job for the next few weeks is far
          wiser.
        </li>
        <li>
          <strong>Vague costings.</strong> "It cost me a few hundred quid" is not evidence. An
          itemised quote or invoice is.
        </li>
        <li>
          <strong>Giving up at the first no.</strong> A rejection is often the council's opening
          position, not the final word. With solid evidence, particularly proof the defect was known,
          a rejection can be challenged.
        </li>
      </ul>

      <h2>Your Evidence Checklist</h2>

      <p>Before you submit, you want to be able to tick off:</p>

      <ul>
        <li>Clear photos of the pothole, with something for scale.</li>
        <li>Photos of the location and surroundings.</li>
        <li>Photos of the damage to your vehicle, ideally linked to the pothole.</li>
        <li>A written repair quote or invoice describing the damage.</li>
        <li>Receipts for any related costs, such as tyres, recovery, or hire car costs.</li>
        <li>Any evidence the pothole was previously reported or long-standing.</li>
        <li>A written record of the incident: location, date, time, and conditions.</li>
        <li>A copy of your own report to the council, with reference number.</li>
      </ul>

      <p>
        Tick most of those and you are no longer hoping for a payout. You are presenting a case that
        is genuinely difficult to refuse.
      </p>

      <h2>The Bottom Line</h2>

      <p>
        A pothole claim stands or falls on what you can show. The good news is that almost all of it
        is gathered in the first few minutes at the roadside and the first few days afterwards, with
        a phone and a bit of method. Treat the evidence as the claim, not an afterthought to it, and
        you give yourself the best possible chance of getting your money back.
      </p>

      <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6">
        <p className="text-sm text-surface-700 leading-relaxed">
          Fixtyer helps UK drivers prepare and submit their own pothole damage claims, without
          solicitors and without commission, so you keep 100% of any compensation. Fixtyer provides
          documents and guidance only and does not provide legal advice. If you are unsure about your
          individual circumstances, seek independent legal advice.
        </p>
      </div>
    </BlogPostLayout>
  );
}
